import React from 'react';

/**
 * Smart Template Engine — Preview System (Phase 1C).
 *
 * Shows how a single full-wall promo video splits across the wall grid: a full_wall
 * item IS one continuous image spanning every screen, so we render ONE media element
 * covering the whole wall and draw the physical screen seams (bezels) on top.
 *
 * (Earlier this rendered N independent <video>s — one per cell — but browsers only
 * decode/play the FIRST of several simultaneous same-source videos, leaving the
 * other cells black. One element fixes that and is also more faithful + drift-free.)
 *
 * Preview-only (illustrative): the real per-cell split is rendered server-side by
 * WallVideoSplitter when the promo is assigned to an actual wall. Read-only,
 * frontend-only — ZERO sync contact.
 *
 * @param {string} src       media URL
 * @param {object} grid      { rows, cols, orientation }
 * @param {number} maxWidth  target wall width in px
 * @param {string} fit       'crop' (cover, default) | 'letterbox' (contain)
 * @param {string} kind      'video' (default) | 'image'
 */
export default function WallVideoSplitPreview({ src, grid, maxWidth = 520, fit = 'crop', kind = 'video' }) {
  const rows = Math.max(1, grid?.rows || 1);
  const cols = Math.max(1, grid?.cols || 1);
  const portrait = grid?.orientation === 'portrait';
  const cellAspect = portrait ? 9 / 16 : 16 / 9; // w/h per screen

  const bezel = 4;
  const cellW = (maxWidth - (cols - 1) * bezel) / cols;
  const cellH = cellW / cellAspect;
  const wallW = cellW * cols + (cols - 1) * bezel;
  const wallH = cellH * rows + (rows - 1) * bezel;
  const objectFit = fit === 'letterbox' ? 'contain' : 'cover';

  if (!src) return null;

  const mediaStyle = { width: wallW, height: wallH, objectFit, display: 'block' };
  const seams = [];
  for (let c = 1; c < cols; c += 1) {
    seams.push(<div key={`v${c}`} style={{ position: 'absolute', top: 0, height: wallH,
      left: c * cellW + (c - 1) * bezel, width: bezel, background: '#000' }} />);
  }
  for (let r = 1; r < rows; r += 1) {
    seams.push(<div key={`h${r}`} style={{ position: 'absolute', left: 0, width: wallW,
      top: r * cellH + (r - 1) * bezel, height: bezel, background: '#000' }} />);
  }

  return (
    <div style={{ background: '#000', padding: bezel, borderRadius: 10, width: 'fit-content' }}>
      <div style={{ position: 'relative', width: wallW, height: wallH, overflow: 'hidden', borderRadius: 6 }}>
        {kind === 'image'
          ? <img src={src} alt="" style={mediaStyle} />
          : <video src={src} muted autoPlay loop playsInline style={mediaStyle} />}
        {seams}
      </div>
    </div>
  );
}
